import React, { useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { differenceInCalendarDays, getUnixTime, addHours } from 'date-fns';
import { useQuery } from '@apollo/client';
import { DragDropContext } from 'react-beautiful-dnd';

import { getTimezoneFromBrowser } from '~/utils/timezone';
import { postsReceived } from '../../../post/slice';
import usePostLimit from '../hooks/usePostLimit';
import useUpdateWeekPostDate from '../hooks/useDropPostInWeek';
import { actions as calendarActions } from '../../reducer';
import DayCells from '../Hours';
import GET_CALENDAR_WEEK_QUERY from '../graphql/week';
import { isDraft as checkIsDraft } from '../../utils/isDraft';
import { useWeeklyDates } from '../hooks/useWeeklyDates';

const findPostById = (posts = [], postId) =>
  posts.find((post) => post.id === postId);

const onDragEnd = ({ result, posts = [], updatePostDueAt }) => {
  const { destination, source, draggableId: postId } = result;

  if (!destination) {
    return;
  }

  // If we drop in the same day cell
  // We don't compare the index here because we check against the timestamp
  if (destination.droppableId === source.droppableId) {
    return;
  }

  const timestamp = Number(destination.droppableId) / 1000;
  const post = findPostById(posts, postId);

  updatePostDueAt({ post, timestamp });
};

const DaysOfWeek = ({
  currentDate,
  parentRef,
  channelsSelected,
  hasTwentyFourHourTimeFormat,
  isViewOnly,
  onDropPostSuccess,
  onDropPostError,
  setOpenModal,
}) => {
  const dispatch = useDispatch();
  const scrollToRef = useRef();
  const currentLocalHour = new Date().getHours();
  const { weekStart, weekEnd } = useWeeklyDates(currentDate);

  const variables = {
    startDate: getUnixTime(weekStart),
    endDate: getUnixTime(weekEnd),
    seed: 10,
    channelIds: channelsSelected,
    timezone: getTimezoneFromBrowser(),
    use24HourTime: hasTwentyFourHourTimeFormat,
    includeDrafts: true,
  };

  const { data, loading, refetch } = useQuery(GET_CALENDAR_WEEK_QUERY, {
    variables,
    onCompleted: ({ calendar }) => {
      dispatch(postsReceived(calendar.posts || []));
    },
    notifyOnNetworkStatusChange: true,
  });

  const shouldRefetch = useSelector((state) => state.calendar.shouldRefetch);
  useEffect(() => {
    if (shouldRefetch && channelsSelected.length > 0) {
      refetch();
      dispatch(calendarActions.clearShouldRefetch());
    }
  }, [shouldRefetch, JSON.stringify(channelsSelected)]);

  const postLimit = usePostLimit(24, currentDate);

  useEffect(() => {
    // Wait for content to finish loading before scrolling because
    // it will most likely change the scroll height of the parent
    if (!loading && scrollToRef?.current && parentRef?.current) {
      // Account for the table header and a bit extra to give it some space
      const offset = scrollToRef.current.offsetTop - 60;
      // Scroll table container to the current hour
      parentRef.current.scrollTo(0, offset);
    }
  }, [scrollToRef?.current, parentRef?.current, loading]);

  const { updatePostDueAt, result: dropResult } = useUpdateWeekPostDate({
    onSuccess: () => {
      const newDate = dropResult.updatePostDueAt.post.dueAt;
      const isDraft = checkIsDraft(dropResult.updatePostDueAt.post.type);
      onDropPostSuccess({ newDate, isDraft });
    },
    onError: () => {
      onDropPostError();
    },
    variables,
  });

  const weekRows = [];
  const numberDaysToDisplay =
    Math.abs(differenceInCalendarDays(weekStart, weekEnd)) + 1;
  // Generates 24 rows, one per hour
  [...Array(24)].forEach((_, i) => {
    const hour = addHours(weekStart, i);
    weekRows.push(
      <tr
        key={`weekly-row-${hour}`}
        data-testid={`weekly-row-${hour}`}
        ref={currentLocalHour === i ? scrollToRef : null}
      >
        <DayCells
          loading={loading}
          hour={i}
          numberDaysToDisplay={numberDaysToDisplay}
          startDate={weekStart}
          data={data}
          is24HourFormat={hasTwentyFourHourTimeFormat}
          postLimit={postLimit}
          isViewOnly={isViewOnly}
          setOpenModal={setOpenModal}
        />
      </tr>
    );
  });

  const posts = data?.calendar?.posts;

  return (
    <DragDropContext
      onDragEnd={(dragResult) =>
        onDragEnd({ result: dragResult, updatePostDueAt, posts })
      }
    >
      {weekRows}
    </DragDropContext>
  );
};

DaysOfWeek.propTypes = {
  isViewOnly: PropTypes.bool,
  hasTwentyFourHourTimeFormat: PropTypes.bool,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  channelsSelected: PropTypes.arrayOf(PropTypes.string),
  onDropPostSuccess: PropTypes.func.isRequired,
  onDropPostError: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollTo: PropTypes.func.isRequired,
    }).isRequired,
  }),
  setOpenModal: PropTypes.func,
};

DaysOfWeek.defaultProps = {
  isViewOnly: true,
  hasTwentyFourHourTimeFormat: false,
  channelsSelected: [],
  parentRef: null,
  setOpenModal: null,
};

export default DaysOfWeek;
